import React, { useMemo, useState } from 'react';
import { Lightbulb, Zap, X } from 'lucide-react';

export interface ExtractedFixture {
  code: string;
  name: string;
  manufacturer: string;
  description: string;
  rowIndex: number;
}

export interface LightingSchedulePreviewModalProps {
  show: boolean;
  extractedLightingFixtures: ExtractedFixture[];
  selectedLightingFixtureIndices: Set<number>;
  lightingScheduleSnapshot: string | null;
  lightingScheduleSnapshots?: string[];
  onToggleSelection: (idx: number) => void;
  onSelectAll: () => void;
  onClose: () => void;
  onCloseFull?: () => void;
  onAddToKeyOnly: () => void;
  onAddToKeyAndCount: () => void;
  guidedMode?: boolean;
  onGuidedContinue?: () => void;
  onGuidedRetry?: () => void;
}

export const LightingSchedulePreviewModal: React.FC<LightingSchedulePreviewModalProps> = ({
  show,
  extractedLightingFixtures,
  selectedLightingFixtureIndices,
  lightingScheduleSnapshot,
  lightingScheduleSnapshots,
  onToggleSelection,
  onSelectAll,
  onClose,
  onCloseFull,
  onAddToKeyOnly,
  onAddToKeyAndCount,
  guidedMode = false,
  onGuidedContinue,
  onGuidedRetry,
}) => {
  const [expandedSnapshot, setExpandedSnapshot] = useState<string | null>(null);
  const sourceSnapshots = useMemo(() => {
    const multiple = (lightingScheduleSnapshots || []).filter((snapshot) => snapshot && snapshot.length > 0);
    if (multiple.length > 0) return multiple;
    return lightingScheduleSnapshot ? [lightingScheduleSnapshot] : [];
  }, [lightingScheduleSnapshot, lightingScheduleSnapshots]);
  const selectedCount = selectedLightingFixtureIndices.size;

  if (!show) return null;

  return (
    <div
      className="lighting-schedule-preview-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100002
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-primary)',
          borderRadius: '16px',
          padding: '28px',
          width: '100%',
          minWidth: '560px',
          maxWidth: '700px',
          maxHeight: '85vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          border: '1px solid var(--border-color)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--border-color)'
        }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Lightbulb size={22} style={{ color: '#f59e0b' }} />
              Fixtures found in schedule
            </h3>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '14px' }}>
              Found {extractedLightingFixtures.length} fixtures from scanned schedule images.
              {selectedLightingFixtureIndices.size < extractedLightingFixtures.length && (
                <span style={{ marginLeft: '8px', color: 'var(--yellow-text)', fontWeight: 500 }}>
                  ({selectedLightingFixtureIndices.size} selected)
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={onSelectAll}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-strong)',
                borderRadius: '6px',
                color: 'var(--yellow-text)',
                cursor: 'pointer',
                padding: '4px 10px',
                fontSize: '12px',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              {selectedLightingFixtureIndices.size === extractedLightingFixtures.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                padding: '8px'
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Snapshot Preview */}
        {sourceSnapshots.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: 500 }}>
              Scanned schedule images ({sourceSnapshots.length}) - Click to enlarge
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '10px',
                maxHeight: '260px',
                overflowY: 'auto',
                paddingRight: '2px',
              }}
            >
              {sourceSnapshots.map((snapshot, index) => (
                <button
                  key={`${snapshot.slice(0, 32)}-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedSnapshot(snapshot);
                  }}
                  style={{
                    padding: 0,
                    border: '1px solid var(--border-color)',
                    borderRadius: '10px',
                    background: 'var(--bg-primary)',
                    cursor: 'zoom-in',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={snapshot}
                    alt={`Lighting schedule source ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '190px',
                      objectFit: 'contain',
                      display: 'block',
                      background: 'var(--bg-secondary)',
                    }}
                  />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Fixture List */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          border: '1px solid var(--border-color)',
          borderRadius: '10px',
          background: 'var(--bg-secondary)'
        }}>
          {extractedLightingFixtures.length > 0 ? (
            extractedLightingFixtures.map((fixture, idx) => {
              const isSelected = selectedLightingFixtureIndices.has(idx);
              return (
                <div
                  key={idx}
                  onClick={() => onToggleSelection(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '14px',
                    padding: '14px 16px',
                    borderBottom: idx < extractedLightingFixtures.length - 1 ? '1px solid var(--border-color)' : 'none',
                    transition: 'all 0.2s',
                    opacity: isSelected ? 1 : 0.4,
                    cursor: 'pointer',
                    background: 'transparent'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    readOnly
                    style={{
                      width: '16px',
                      height: '16px',
                      cursor: 'pointer',
                      accentColor: '#f59e0b',
                      flexShrink: 0,
                      marginTop: '16px'
                    }}
                  />
                  <div style={{
                    minWidth: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, var(--yellow-tint-medium) 0%, var(--yellow-tint-strong) 100%)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '16px',
                    color: 'var(--yellow-text-dark)',
                    border: '2px solid #f59e0b',
                    flexShrink: 0
                  }}>
                    {fixture.code}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', lineHeight: '1.3' }}>
                      {fixture.name}
                    </h4>
                    {fixture.manufacturer && (
                      <p style={{ margin: '0 0 4px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                        Manufacturer: {fixture.manufacturer}
                      </p>
                    )}
                    <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4', wordBreak: 'break-word' }}>
                      {fixture.description}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-placeholder)' }}>
              <p style={{ fontSize: '16px', marginBottom: '8px' }}>No fixtures extracted</p>
              <p style={{ fontSize: '13px' }}>Try selecting a clearer area of the lighting schedule</p>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div style={{
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          {guidedMode ? (
            <>
              <button
                onClick={onGuidedRetry ?? onClose}
                style={{
                  padding: '10px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginRight: 'auto',
                }}
              >
                Try Again
              </button>
              <button
                onClick={onGuidedContinue ?? onAddToKeyOnly}
                disabled={selectedCount === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedCount > 0 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedCount > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Zap size={16} />
                Extract and Continue ({selectedCount})
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onCloseFull ?? onClose}
                style={{
                  padding: '10px 20px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={onAddToKeyOnly}
                disabled={selectedCount === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedCount > 0 ? 'var(--bg-primary)' : 'var(--bg-tertiary)',
                  border: selectedCount > 0 ? '2px solid #f59e0b' : '1px solid var(--border-strong)',
                  borderRadius: '8px',
                  color: selectedCount > 0 ? '#d97706' : 'var(--text-placeholder)',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedCount > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Lightbulb size={16} />
                Add to Key Only ({selectedCount})
              </button>
              <button
                onClick={onAddToKeyAndCount}
                disabled={selectedCount === 0}
                style={{
                  padding: '10px 20px',
                  background: selectedCount > 0 ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'var(--border-strong)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: selectedCount > 0 ? 'pointer' : 'not-allowed',
                  opacity: selectedCount > 0 ? 1 : 0.6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Zap size={16} />
                Add & Count ({selectedCount})
              </button>
            </>
          )}
        </div>

        {expandedSnapshot && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.78)',
              zIndex: 100010,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '28px',
            }}
            onClick={(event) => {
              event.stopPropagation();
              setExpandedSnapshot(null);
            }}
          >
            <button
              onClick={(event) => {
                event.stopPropagation();
                setExpandedSnapshot(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                border: 'none',
                background: 'rgba(17, 24, 39, 0.75)',
                color: '#fff',
                borderRadius: '9999px',
                width: '38px',
                height: '38px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              aria-label="Close enlarged schedule image"
            >
              <X size={20} />
            </button>
            <img
              src={expandedSnapshot}
              alt="Enlarged lighting schedule source"
              onClick={(event) => event.stopPropagation()}
              style={{
                maxWidth: '95vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                background: '#0f172a',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
